<template>
    <!-- the shooting of one match: a strip of percentages and the points split by shot type -->
    <div class="shot-summary">
        <div class="shot-summary__strip">
            <div v-for="cell in cells" :key="cell.code" class="shot-summary__cell" :class="{ 'shot-summary__cell--lead': cell.code === 'FG' }">
                <span class="shot-summary__code">{{ cell.code }}</span>
                <span class="shot-summary__pct">{{ cell.att ? `${cell.pct}%` : '–' }}</span>
                <span class="shot-summary__ratio">{{ cell.made }}/{{ cell.att }}</span>
            </div>
        </div>

        <div class="shot-summary__points">
            <div class="shot-summary__points-head">
                <span><b>{{ points.total }}</b> πόντοι</span>
                <!-- room for more, e.g. the TS% of the page -->
                <slot />
            </div>
            <div v-if="points.total" class="shot-summary__bar">
                <span
                    v-for="part in points.parts"
                    :key="part.code"
                    :class="`shot-summary__part--${part.code}`"
                    :style="{ width: `${part.share}%` }"
                >
                    <info-tip :text="`${part.label}: ${part.points} πόντοι (${part.share}%)`" block />
                </span>
            </div>
            <div v-if="points.total" class="shot-summary__legend">
                <span v-for="part in points.parts" :key="part.code">
                    <i :class="`shot-summary__part--${part.code}`" />{{ part.label }} <b>{{ part.points }}</b>
                </span>
            </div>
        </div>
    </div>
</template>

<script>
    const num = value => Number(value) || 0

    export default {
        props: {
            // a line of stats: twoPointsMade, twoPointAttempts, threePointsMade, ...
            stat: {
                type: Object,
                default: () => ({})
            }
        },
        computed: {
            cells() {
                const s = this.stat || {}
                const cell = (code, made, att) => ({ code, made, att, pct: att ? Math.round(made / att * 100) : 0 })
                return [
                    cell('FG', num(s.twoPointsMade) + num(s.threePointsMade), num(s.twoPointAttempts) + num(s.threePointAttempts)),
                    cell('2P', num(s.twoPointsMade), num(s.twoPointAttempts)),
                    cell('3P', num(s.threePointsMade), num(s.threePointAttempts)),
                    cell('FT', num(s.freeThrowsMade), num(s.freeThrowAttempts))
                ]
            },
            points() {
                const s = this.stat || {}
                const parts = [
                    { code: 'two', label: '2P', points: num(s.twoPointsMade) * 2 },
                    { code: 'three', label: '3P', points: num(s.threePointsMade) * 3 },
                    { code: 'ft', label: 'Βολές', points: num(s.freeThrowsMade) }
                ]
                const total = parts.reduce((sum, part) => sum + part.points, 0)
                return { total, parts: parts.map(part => ({ ...part, share: total ? Math.round(part.points / total * 100) : 0 })) }
            }
        }
    }
</script>

<style scoped>
.shot-summary {
    margin: 10px 0 12px;
    font-family: Ubuntu, Arial, sans-serif;
}

/* ---------- the strip: one cell per shot type ---------- */
.shot-summary__strip {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    border-radius: 10px;
    background: #f4f4f6;
    overflow: hidden;
}

.shot-summary__cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 7px 2px 6px;
    line-height: 1.15;
}

.shot-summary__cell + .shot-summary__cell {
    border-left: 1px solid rgba(0, 0, 0, 0.07);
}

.shot-summary__cell--lead {
    background: linear-gradient(135deg, #ef4444 0%, #ff7133 55%, #ffa000 100%);
    color: #fff;
}

.shot-summary__code {
    font-size: 0.66rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: rgba(0, 0, 0, 0.45);
}

.shot-summary__cell--lead .shot-summary__code {
    color: rgba(255, 255, 255, 0.85);
}

.shot-summary__pct {
    font-size: 1.1rem;
    font-weight: 800;
}

.shot-summary__ratio {
    font-size: 0.7rem;
    color: rgba(0, 0, 0, 0.5);
}

.shot-summary__cell--lead .shot-summary__ratio {
    color: rgba(255, 255, 255, 0.85);
}

/* ---------- where the points came from ---------- */
.shot-summary__points {
    margin-top: 10px;
}

.shot-summary__points-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 5px;
    font-size: 0.82rem;
    color: rgba(0, 0, 0, 0.6);
}

.shot-summary__points-head b {
    color: rgba(0, 0, 0, 0.87);
    font-size: 1rem;
}

.shot-summary__bar {
    display: flex;
    gap: 2px;
    height: 8px;
    border-radius: 999px;
    overflow: hidden;
}

.shot-summary__legend {
    display: flex;
    gap: 12px;
    margin-top: 5px;
    font-size: 0.72rem;
    color: rgba(0, 0, 0, 0.55);
}

.shot-summary__legend span {
    display: inline-flex;
    align-items: center;
    gap: 4px;
}

.shot-summary__legend b {
    color: rgba(0, 0, 0, 0.87);
}

.shot-summary__legend i {
    width: 8px;
    height: 8px;
    border-radius: 50%;
}

.shot-summary__part--two {
    background: #ff7133;
}

.shot-summary__part--three {
    background: #ef4444;
}

.shot-summary__part--ft {
    background: #ffc107;
}
</style>
